/**
 * @fileoverview DropdownRenderer component for the Storyteller web application.
 * Renders a dropdown/select input with configurable options from a JSON schema.
 * Supports both string and number values, with optional translations and icons.
 *
 * @example
 * // Basic usage
 * <DropdownRenderer
 *   schema={{
 *     type: 'string',
 *     oneOf: [
 *       { const: 'option1', title: 'Option 1' },
 *       { const: 'option2', title: 'Option 2' }
 *     ]
 *   }}
 *   uischema={{
 *     type: 'Control',
 *     options: { name: 'dropdownName' }
 *   }}
 * />
 */


import {
    ControlProps,
    JsonSchema4,
    and,
    isNumberControl,
    isStringControl,
    or,
    rankWith,
    schemaMatches,
} from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from '../../../base/Dropdown'

/**
 * @interface DropdownRendererProps
 * @description Props for the DropdownRenderer component.
 * @extends {ControlProps}
 * @property {string} label - The label for the dropdown.
 * @property {boolean} required - Whether the dropdown is required.
 * @property {function} handleChange - The function to handle the change of the dropdown value.
 * @property {string} path - The path of the dropdown in the form data.
 */
interface DropdownRendererProps extends ControlProps {
    label: string
    required?: boolean
    handleChange(path: string, value: string): void
    path: string
}

/**
 * @function DropdownRenderer
 * @description Component for rendering a dropdown/select input.
 * @param {DropdownRendererProps} props - The props for the DropdownRenderer component.
 * @returns {JSX.Element} The DropdownRenderer component.
 */
function DropdownRenderer(props: DropdownRendererProps) {
    const { schema, data, uischema, errors, handleChange, path, required } = props
    const { t } = useTranslation()

    const value = data || schema.default || ''

    function onChange(value: unknown) {
        let formattedValue = value
        if (schema.type === 'number') {
            formattedValue = parseInt(value as string)
        }

        const selected = (schema.oneOf as JsonSchema4[]).find(
            (option: JsonSchema4) => option.const === formattedValue
        )
        if (selected) {
            handleChange(path, selected.const)
        }
    }

    const options = (schema.oneOf as Record<string, string>[]).map((option) => ({
        title: t(`options.${uischema.options?.name}.${option.const}`),
        const: option.const,
        icon: option.icon,
    }))

    return (
        <Dropdown
            value={value}
            onChange={onChange}
            errors={errors}
            required={required}
            placeholder={t(uischema.options?.placeholder)}
            options={options}
            path={path}
        />
    )
}

/**
 * @function DropdownTester
 * @description Ranker for the DropdownRenderer component.
 * @param {RankingTester} rankWith - The rankWith function from @jsonforms/core.
 * @returns {RankingTester} The DropdownTester function.
 */
export const DropdownTester = rankWith(
    4, //increase rank as needed
    and(
        schemaMatches((schema) => Object.prototype.hasOwnProperty.call(schema, 'oneOf')),
        or(isNumberControl, isStringControl)
    )
)

export default withJsonFormsControlProps(DropdownRenderer)
